import { ticketType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { format, parseISO } from "date-fns"
import { useAtomValue } from "jotai"
import { UserRound } from "lucide-react"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { resolveOperationRule } from "@/api/client/operation-rule"
import { ownerApi, ownerProperties } from "@/api/client/owner"
import { ticketApi, ticketSchemas } from "@/api/client/ticket"
import type { TicketAddProgressBody, TicketGet200Ticket } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import {
  type ContactCategory,
  ContactPersonSelector,
} from "@/components/ticket/contact-person-selector"
import { ContactTypeSelector } from "@/components/ticket/contact-type-selector"
import { OcAssigneeSelector } from "@/components/ticket/oc-assignee-selector"
import {
  type TicketCategory,
  TicketCategorySelector,
  type TicketSubCategory,
} from "@/components/ticket/ticket-category-selector"
import { TicketStatusSelector } from "@/components/ticket/ticket-status-selector"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import { DateTimePickerField } from "@/components/ui/date-time-picker"
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DraggableDialogContent,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { RequiredBadge } from "@/components/ui/required-badge"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { sessionUserAtom } from "@/store/session"
import { DEFAULT_CONTRACTOR_NOTE, DEFAULT_TICKET_CONTACT_CONTENT } from "../constants"

// APIスキーマをベースにフォーム用スキーマを定義
// フォーム用IDフィールドを追加
const ticketProgressFormSchema = ticketSchemas.ticketAddProgressBody
  .omit({
    // API用のID系フィールドはフォームでは contactFromId/contactToId で統合管理するため除外
    contactFromOwnerId: true,
    contactFromTenantId: true,
    contactFromAdminId: true,
    contactToOwnerId: true,
    contactToTenantId: true,
    contactToAdminId: true,
  })
  .extend({
    // 必須フィールドにバリデーションメッセージを追加
    title: z.string().trim().min(1, "案件タイトルは必須です"),
    category: z.string().min(1, "大カテゴリは必須です"),
    subCategory: z.string().min(1, "中カテゴリは必須です"),
    contactType: z.string().min(1, "連絡手段は必須です"),
    contactFromCategory: z.string().min(1, "連絡者区分（FROM）は必須です"),
    contactToCategory: z.string().min(1, "連絡者区分（TO）は必須です"),
    content: z.string().min(1, "連絡内容は必須です"),
    ocAssigneeId: z.string().min(1, "OC担当者名は必須です"),
    status: z.string().min(1, "対応後ステータスは必須です"),
    // 日時フィールド（ISO文字列形式）
    contactAt: z.string().min(1, "連絡日時は必須です"),
    alertAt: z.string().optional(),
    // フォーム用の統合IDフィールド（category に応じて API側の適切なフィールドに変換）
    contactFromId: z.string().optional(),
    contactToId: z.string().optional(),
    // API側がnullishのフィールドをフォーム用に文字列として上書き（空文字列を許容）
    contactFromText: z.string().optional(),
    contactToText: z.string().optional(),
    alertComment: z.string().max(1000).optional(),
    ocComment: z.string().max(1000).optional(),
    contractorStatus: z.string().optional(),
    contractorType: z.string().optional(),
    contractorContact: z.string().max(255).optional(),
    contractorContactInfo: z.string().max(500).optional(),
    contractorScheduledDate: z.string().optional(),
    contractorNote: z.string().max(1000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.contactFromCategory && !data.contactFromId && !data.contactFromText?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactFromText"],
        message: "その他（FROM）は必須です",
      })
    }
    if (data.contactToCategory && !data.contactToId && !data.contactToText?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["contactToText"],
        message: "その他（TO）は必須です",
      })
    }
  })

type TicketProgressFormData = z.infer<typeof ticketProgressFormSchema>
export type TicketProgressTransferInitialValues = Partial<TicketProgressFormData>

// フォームデータをAPI型に変換
const toApiPayload = (data: TicketProgressFormData): TicketAddProgressBody => {
  // FROM側のID設定
  const contactFromIds: {
    contactFromOwnerId?: string | null
    contactFromTenantId?: number | null
    contactFromAdminId?: string | null
    contactFromText?: string | null
  } = {}

  if (data.contactFromCategory === "owner" && data.contactFromId) {
    contactFromIds.contactFromOwnerId = data.contactFromId
  } else if (data.contactFromCategory === "tenant" && data.contactFromId) {
    contactFromIds.contactFromTenantId = Number.parseInt(data.contactFromId, 10)
  } else if (data.contactFromCategory === "oc" && data.contactFromId) {
    contactFromIds.contactFromAdminId = data.contactFromId
  }
  if (data.contactFromText?.trim()) {
    contactFromIds.contactFromText = data.contactFromText
  }

  // TO側のID設定
  const contactToIds: {
    contactToOwnerId?: string | null
    contactToTenantId?: number | null
    contactToAdminId?: string | null
    contactToText?: string | null
  } = {}

  if (data.contactToCategory === "owner" && data.contactToId) {
    contactToIds.contactToOwnerId = data.contactToId
  } else if (data.contactToCategory === "tenant" && data.contactToId) {
    contactToIds.contactToTenantId = Number.parseInt(data.contactToId, 10)
  } else if (data.contactToCategory === "oc" && data.contactToId) {
    contactToIds.contactToAdminId = data.contactToId
  }
  if (data.contactToText?.trim()) {
    contactToIds.contactToText = data.contactToText
  }

  return {
    title: data.title,
    category: data.category as TicketAddProgressBody["category"],
    subCategory: data.subCategory as TicketAddProgressBody["subCategory"],
    status: data.status as TicketAddProgressBody["status"],
    contactAt: data.contactAt,
    contactType: data.contactType as TicketAddProgressBody["contactType"],
    contactFromCategory: data.contactFromCategory as TicketAddProgressBody["contactFromCategory"],
    ...contactFromIds,
    contactToCategory: data.contactToCategory as TicketAddProgressBody["contactToCategory"],
    ...contactToIds,
    content: data.content,
    alertAt: data.alertAt || undefined,
    alertComment: data.alertComment || undefined,
    ocComment: data.ocComment || undefined,
    ocAssigneeId: data.ocAssigneeId || null,
    contractorStatus:
      data.contractorStatus && data.contractorStatus !== "none"
        ? (data.contractorStatus as TicketAddProgressBody["contractorStatus"])
        : undefined,
    contractorType:
      data.contractorType && data.contractorType !== "none"
        ? (data.contractorType as TicketAddProgressBody["contractorType"])
        : undefined,
    contractorName: data.contractorName || undefined,
    contractorContact: data.contractorContact || undefined,
    contractorContactInfo: data.contractorContactInfo || undefined,
    contractorScheduledDate: data.contractorScheduledDate || undefined,
    contractorNote: data.contractorNote || undefined,
  }
}

// 日付文字列をDateオブジェクトに変換
const parseDateString = (dateString: string | undefined): Date | undefined => {
  if (!dateString) return undefined
  try {
    return parseISO(dateString)
  } catch {
    return undefined
  }
}

// DateオブジェクトをYYYY-MM-DD形式の文字列に変換
const formatDateToString = (date: Date | undefined): string => {
  if (!date) return ""
  return format(date, "yyyy-MM-dd")
}

interface TicketProgressAddModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ticketId: string
  ticket: TicketGet200Ticket
  onSuccess?: () => void
  transferInitialValues?: TicketProgressTransferInitialValues | null
}

export function TicketProgressAddModal({
  open,
  onOpenChange,
  ticketId,
  ticket,
  onSuccess,
  transferInitialValues,
}: TicketProgressAddModalProps) {
  const queryClient = useQueryClient()
  const sessionUser = useAtomValue(sessionUserAtom)
  const currentAdminName = sessionUser?.name || sessionUser?.email || ""
  const warningItems: string[] = []
  if (ticket.property?.note) warningItems.push("物件注意事項の注意事項がございます。")
  if (ticket.tenant?.adminMemo) warningItems.push("賃借人注意事項の注意事項がございます。")
  if (ticket.owner?.adminMemo) warningItems.push("オーナー注意事項の注意事項がございます。")
  if (ticket.ocComment) warningItems.push("OCコメントの注意事項がございます。")

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TicketProgressFormData>({
    resolver: zodResolver(ticketProgressFormSchema),
    defaultValues: {
      // 案件情報から初期値を設定
      title: ticket.title || "",
      category: ticket.category || "",
      subCategory: ticket.subCategory || "",
      // 連絡日時は空（毎回入力）
      contactAt: "",
      contactType: "",
      contactFromCategory: "",
      contactFromId: "",
      contactFromText: "",
      contactToCategory: "",
      contactToId: "",
      contactToText: "",
      content: DEFAULT_TICKET_CONTACT_CONTENT,
      // アラート日時を案件情報から設定（ISO文字列形式）
      alertAt: ticket.alertAt || "",
      alertComment: ticket.alertComment || "",
      // OCコメント・担当者を案件情報から設定
      ocComment: ticket.ocComment || "",
      ocAssigneeId: sessionUser?.id ?? ticket.ocAssigneeId ?? "",
      // 対応後ステータスを案件情報から設定
      status: ticket.status || "",
      // 業者関連を案件情報から設定
      contractorStatus: ticket.contractorStatus || "none",
      contractorType: ticket.contractorType || "none",
      contractorName: ticket.contractorName || "",
      contractorContact: ticket.contractorContact || "",
      contractorContactInfo: ticket.contractorContactInfo || "",
      contractorScheduledDate: ticket.contractorScheduledDate || "",
      contractorNote: ticket.contractorNote || DEFAULT_CONTRACTOR_NOTE,
    },
  })

  // 手配先業者で「オーナー様指定業者」が選ばれた場合、その物件に適用される運用ルール
  // (物件固有→オーナー既定でフォールバック解決)の指定業者から選べるようにする(E-13)。
  const [selectedDesignatedId, setSelectedDesignatedId] = useState("")
  const isOwnerSpecified = watch("contractorType") === ticketType.CONTRACTOR_TYPE_OWNER_SPECIFIED
  const ticketOwnerId = ticket.owner?.id
  const ticketPropertyId = ticket.property?.id
  const { data: ownerData } = ownerApi.useOwnerGet(
    ticketOwnerId ?? "",
    { withOperationRules: ownerProperties.OwnerGetWithOperationRules.true },
    {
      query: {
        enabled: isOwnerSpecified && !!ticketOwnerId && ticketPropertyId != null,
      },
    }
  )
  const designatedContractors =
    resolveOperationRule(ownerData?.owner.operationRules, ticketPropertyId)
      ?.designatedContractors ?? []

  useEffect(() => {
    if (!open) return

    const defaultValues: TicketProgressFormData = {
      title: ticket.title || "",
      category: ticket.category || "",
      subCategory: ticket.subCategory || "",
      contactAt: "",
      contactType: "",
      contactFromCategory: "",
      contactFromId: "",
      contactFromText: "",
      contactToCategory: "",
      contactToId: "",
      contactToText: "",
      content: DEFAULT_TICKET_CONTACT_CONTENT,
      alertAt: ticket.alertAt || "",
      alertComment: ticket.alertComment || "",
      ocComment: ticket.ocComment || "",
      ocAssigneeId: sessionUser?.id ?? ticket.ocAssigneeId ?? "",
      status: ticket.status || "",
      contractorStatus: ticket.contractorStatus || "none",
      contractorType: ticket.contractorType || "none",
      contractorName: ticket.contractorName || "",
      contractorContact: ticket.contractorContact || "",
      contractorContactInfo: ticket.contractorContactInfo || "",
      contractorScheduledDate: ticket.contractorScheduledDate || "",
      contractorNote: ticket.contractorNote || DEFAULT_CONTRACTOR_NOTE,
    }

    reset({
      ...defaultValues,
      ...transferInitialValues,
    })
  }, [open, reset, sessionUser?.id, ticket, transferInitialValues])

  const handleSelectDesignatedContractor = (value: string) => {
    setSelectedDesignatedId(value)
    // 業者はルール内の配列順で識別する
    const contractor = designatedContractors[Number(value)]
    if (contractor) {
      // 選択時点の業者情報を ticket にスナップショット保存する(設計確定事項)。
      setValue("contractorName", contractor.name)
      setValue("contractorContact", contractor.contactPerson || "")
      setValue("contractorContactInfo", contractor.contactInfo)
    }
  }

  const addProgressMutation = ticketApi.useTicketAddProgress({
    mutation: {
      onSuccess: () => {
        toast.success("進捗を追加しました")
        queryClient.invalidateQueries({ queryKey: ticketApi.getTicketGetQueryKey(ticketId) })
        reset()
        onOpenChange(false)
        onSuccess?.()
      },
      onError: (error) => {
        toast.error("進捗の追加に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleFormSubmit = (data: TicketProgressFormData) => {
    const payload = toApiPayload(data)
    addProgressMutation.mutate({ id: ticketId, data: payload })
  }

  const handleAlertReset = () => {
    setValue("alertAt", "")
    setValue("alertComment", "")
  }

  const handleContractorScheduledDateClear = () => {
    setValue("contractorScheduledDate", "")
  }

  const setFromCurrentAdmin = () => {
    if (!sessionUser?.id) return
    setValue("contactFromCategory", "oc" as TicketProgressFormData["contactFromCategory"])
    setValue("contactFromId", sessionUser.id)
    setValue("contactFromText", "")
  }

  const setToCurrentAdmin = () => {
    if (!sessionUser?.id) return
    setValue("contactToCategory", "oc" as TicketProgressFormData["contactToCategory"])
    setValue("contactToId", sessionUser.id)
    setValue("contactToText", "")
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DraggableDialogContent className="max-w-[620px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="text-[18px] font-semibold">進捗追加</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              追加したい進捗情報を入力してください
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-4">
            {/* 案件タイトル */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">案件タイトル</p>
                <RequiredBadge />
              </div>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <Input {...field} placeholder="案件タイトル" />}
              />
              {errors.title && <p className="text-xs text-red-500">{errors.title.message}</p>}
            </div>

            {/* カテゴリ */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">案件カテゴリ</p>
                <RequiredBadge />
              </div>
              <Controller
                name="category"
                control={control}
                render={({ field: categoryField }) => (
                  <Controller
                    name="subCategory"
                    control={control}
                    render={({ field: subCategoryField }) => (
                      <TicketCategorySelector
                        categoryValue={categoryField.value as TicketCategory | ""}
                        subCategoryValue={subCategoryField.value as TicketSubCategory | ""}
                        onCategoryChange={(value) => {
                          categoryField.onChange(value)
                          subCategoryField.onChange("")
                        }}
                        onSubCategoryChange={(value) => subCategoryField.onChange(value)}
                        categoryError={errors.category?.message}
                        subCategoryError={errors.subCategory?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            {/* 連絡日時 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">連絡日時</p>
                <RequiredBadge />
              </div>
              <Controller
                name="contactAt"
                control={control}
                render={({ field }) => (
                  <DateTimePickerField value={field.value} onChange={field.onChange} required />
                )}
              />
              {errors.contactAt && (
                <p className="text-xs text-red-500">{errors.contactAt.message}</p>
              )}
            </div>

            {/* 連絡手段 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">連絡手段</p>
                  <RequiredBadge />
                </div>
                <Controller
                  name="contactType"
                  control={control}
                  render={({ field }) => (
                    <ContactTypeSelector
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="連絡手段"
                      error={errors.contactType?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* 連絡者（FROM） */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">連絡者（FROM）</p>
                <RequiredBadge />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-[#354267]"
                  onClick={setFromCurrentAdmin}
                  disabled={!sessionUser?.id}
                  aria-label="ログインユーザーを連絡者（FROM）に入力"
                  title="ログインユーザーを入力"
                >
                  <UserRound size={14} />
                </Button>
              </div>
              <ContactPersonSelector
                direction="from"
                categoryValue={watch("contactFromCategory") as ContactCategory | ""}
                personIdValue={watch("contactFromId") || ""}
                textValue={watch("contactFromText") || ""}
                onCategoryChange={(value) => {
                  setValue(
                    "contactFromCategory",
                    value as TicketProgressFormData["contactFromCategory"]
                  )
                  // カテゴリ変更時にIDをリセット
                  setValue("contactFromId", "")
                  setValue("contactFromText", "")

                  // チケット情報が存在する場合、自動的に該当する連絡者を選択
                  if (value === "owner" && ticket.owner?.id) {
                    setValue("contactFromId", ticket.owner.id)
                  } else if (value === "tenant" && ticket.tenantId) {
                    setValue("contactFromId", String(ticket.tenantId))
                  } else if (value === "oc" && sessionUser?.id) {
                    setValue("contactFromId", sessionUser.id)
                  }
                }}
                onPersonIdChange={(value) => setValue("contactFromId", value)}
                onTextChange={(value) => setValue("contactFromText", value)}
                categoryError={errors.contactFromCategory?.message}
                textError={errors.contactFromText?.message}
                selectedOwnerId={ticket.owner?.id}
                selectedOwnerName={ticket.owner?.name}
                selectedTenantId={ticket.tenantId ?? undefined}
                selectedTenantName={ticket.tenant?.name}
                currentAdminId={sessionUser?.id}
                currentAdminName={currentAdminName}
                includeEmergency
              />
            </div>

            {/* 連絡者（TO） */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">連絡者（TO）</p>
                <RequiredBadge />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-[#354267]"
                  onClick={setToCurrentAdmin}
                  disabled={!sessionUser?.id}
                  aria-label="ログインユーザーを連絡者（TO）に入力"
                  title="ログインユーザーを入力"
                >
                  <UserRound size={14} />
                </Button>
              </div>
              <ContactPersonSelector
                direction="to"
                categoryValue={watch("contactToCategory") as ContactCategory | ""}
                personIdValue={watch("contactToId") || ""}
                textValue={watch("contactToText") || ""}
                onCategoryChange={(value) => {
                  setValue(
                    "contactToCategory",
                    value as TicketProgressFormData["contactToCategory"]
                  )
                  // カテゴリ変更時にIDをリセット
                  setValue("contactToId", "")
                  setValue("contactToText", "")

                  // チケット情報が存在する場合、自動的に該当する連絡者を選択
                  if (value === "owner" && ticket.owner?.id) {
                    setValue("contactToId", ticket.owner.id)
                  } else if (value === "tenant" && ticket.tenantId) {
                    setValue("contactToId", String(ticket.tenantId))
                  } else if (value === "oc" && sessionUser?.id) {
                    setValue("contactToId", sessionUser.id)
                  }
                }}
                onPersonIdChange={(value) => setValue("contactToId", value)}
                onTextChange={(value) => setValue("contactToText", value)}
                categoryError={errors.contactToCategory?.message}
                textError={errors.contactToText?.message}
                selectedOwnerId={ticket.owner?.id}
                selectedOwnerName={ticket.owner?.name}
                selectedTenantId={ticket.tenantId ?? undefined}
                selectedTenantName={ticket.tenant?.name}
                currentAdminId={sessionUser?.id}
                currentAdminName={currentAdminName}
                includeEmergency
              />
            </div>

            {/* 連絡内容 */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold">連絡内容</p>
                <RequiredBadge />
              </div>
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={12}
                    className="resize-y"
                    placeholder="連絡内容を入力してください"
                  />
                )}
              />
              {errors.content && <p className="text-xs text-red-500">{errors.content.message}</p>}
            </div>

            {/* 注意事項 */}
            {warningItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold">注意事項</p>
                <div className="inline-flex bg-[#ffeeee] border border-[#ff3232] rounded-md p-3">
                  <div className="text-xs space-y-1">
                    {warningItems.map((item) => (
                      <p key={item}>・{item}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* アラート日時 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold">アラート日時</p>
                <Button type="button" variant="outline" size="sm" onClick={handleAlertReset}>
                  アラートリセット
                </Button>
              </div>
              <Controller
                name="alertAt"
                control={control}
                render={({ field }) => (
                  <DateTimePickerField
                    value={field.value}
                    onChange={field.onChange}
                    defaultTime="00:00"
                  />
                )}
              />
            </div>

            {/* アラートコメント */}
            <div className="space-y-2">
              <p className="text-xs font-semibold">アラートコメント</p>
              <Controller
                name="alertComment"
                control={control}
                render={({ field }) => <Input {...field} placeholder="アラートコメント" />}
              />
            </div>

            {/* OCコメント */}
            <div className="space-y-2">
              <p className="text-xs font-semibold">OCコメント</p>
              <Controller
                name="ocComment"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={3}
                    className="resize-y"
                    placeholder="OCコメントを入力してください"
                  />
                )}
              />
            </div>

            {/* OC担当者名 */}
            <div className="w-1/2 pr-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">OC担当者名</p>
                  <RequiredBadge />
                </div>
                <Controller
                  name="ocAssigneeId"
                  control={control}
                  render={({ field }) => (
                    <OcAssigneeSelector
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.ocAssigneeId?.message}
                      placeholder="担当者を選択"
                    />
                  )}
                />
              </div>
            </div>

            {/* 対応後ステータス */}
            <div className="w-1/2 pr-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">対応後ステータス</p>
                  <RequiredBadge />
                </div>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TicketStatusSelector
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="ステータスを選択"
                      error={errors.status?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* 業者依頼状況 */}
            <div className="w-1/2 pr-2">
              <div className="space-y-2">
                <p className="text-xs font-semibold">業者依頼状況</p>
                <Controller
                  name="contractorStatus"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || "none"}
                      onChange={field.onChange}
                      placeholder="依頼状況を選択"
                      options={[
                        { value: "none", label: "未選択" },
                        ...ticketType.CONTRACTOR_STATUSES.map(({ key, value }) => ({
                          value: key,
                          label: value,
                        })),
                      ]}
                    />
                  )}
                />
              </div>
            </div>

            {/* 手配先業者・業者名 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold">手配先業者</p>
                <Controller
                  name="contractorType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || "none"}
                      onChange={field.onChange}
                      placeholder="業者を選択"
                      options={[
                        { value: "none", label: "未選択" },
                        ...ticketType.CONTRACTOR_TYPES.map(({ key, value }) => ({
                          value: key,
                          label: value,
                        })),
                      ]}
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold">業者名</p>
                <Controller
                  name="contractorName"
                  control={control}
                  render={({ field }) => <Input {...field} placeholder="業者名を入力" />}
                />
              </div>
            </div>

            {/* オーナー様指定業者を選択(E-13): 物件に適用される運用ルールの指定業者から選ぶ */}
            {isOwnerSpecified && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-semibold">オーナー様指定業者から選択</p>
                  <Select
                    value={selectedDesignatedId || "none"}
                    onChange={handleSelectDesignatedContractor}
                    placeholder="指定業者を選択"
                    options={[
                      { value: "none", label: "未選択" },
                      ...designatedContractors.map((dc, index) => ({
                        value: String(index),
                        label: dc.name,
                      })),
                    ]}
                  />
                  {designatedContractors.length === 0 && (
                    <p className="text-xs text-slate-400">
                      この物件・オーナーに登録された指定業者はありません
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* 業者担当者 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold">業者担当者</p>
                <Controller
                  name="contractorContact"
                  control={control}
                  render={({ field }) => <Input {...field} placeholder="担当者を入力" />}
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold">業者連絡先</p>
                <Controller
                  name="contractorContactInfo"
                  control={control}
                  render={({ field }) => <Input {...field} placeholder="連絡先を入力" />}
                />
              </div>
            </div>

            {/* 業者対応予定日 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold">業者対応予定日</p>
                <Controller
                  name="contractorScheduledDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      value={parseDateString(field.value)}
                      onChange={(date) => field.onChange(formatDateToString(date))}
                      placeholder="日付を選択"
                    />
                  )}
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold">&nbsp;</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleContractorScheduledDateClear}
                >
                  クリア
                </Button>
              </div>
            </div>

            {/* 業者備考 */}
            <div className="space-y-2">
              <p className="text-xs font-semibold">業者備考</p>
              <Controller
                name="contractorNote"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    rows={3}
                    className="resize-none"
                    placeholder="業者備考を入力してください"
                  />
                )}
              />
            </div>

            {/* ボタン */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="submit"
                loading={addProgressMutation.isPending}
                className="bg-[#354267] hover:bg-[#354267]/90"
              >
                追加する
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                キャンセル
              </Button>
            </div>
          </form>
        </div>
      </DraggableDialogContent>
    </Dialog>
  )
}
